import { Input } from '@shared/components/ui';
import {
  Database,
  Server,
  Check,
  AlertCircle,
  Loader2,
  ChevronDown,
  FolderOpen,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Template } from './TemplateSelection';
import { FolderBrowserModal } from '../FolderBrowserModal';

export interface Agent {
  id: string;
  name: string;
  hostname?: string;
  status?: string;
}

interface BasicDetailsProps {
  template: Template;
  data: {
    name: string;
    agent_id: string;
    host?: string;
    port?: number;
    username?: string;
    password?: string;
    database?: string;
    path?: string;
    useVSS?: boolean;
  };
  onChange: (data: Record<string, unknown>) => void;
  onNext: () => void;
  onBack: () => void;
  agents?: Agent[];
  isCreating?: boolean;
}

interface TestConnectionResult {
  success: boolean;
  databases?: string[];
  error?: string;
  server_version?: string;
}

export function BasicDetails({
  template,
  data,
  onChange,
  onNext,
  onBack,
  agents: propAgents,
  isCreating,
}: BasicDetailsProps) {
  const { t } = useTranslation('backups');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [agents, setAgents] = useState<Agent[]>(propAgents || []);
  const [loadingAgents, setLoadingAgents] = useState(!propAgents);
  const [agentsLoadFailed, setAgentsLoadFailed] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isFolderBrowserOpen, setIsFolderBrowserOpen] = useState(false);

  useEffect(() => {
    if (!propAgents) {
      fetchAgents();
    }
  }, [propAgents]);

  useEffect(() => {
    // Enable VSS by default for file backups
    if (template.type === 'FILES' && data.useVSS === undefined) {
      onChange({ useVSS: true });
    }
  }, [template.type, data.useVSS, onChange]);

  const fetchAgents = async () => {
    setLoadingAgents(true);
    setAgentsLoadFailed(false);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/v1/agents', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Agents request failed with status ${res.status}`);
      const result = await res.json();
      // API returns agents as direct array, not wrapped in 'agents' property
      const agentsList = Array.isArray(result) ? result : result.agents || [];
      setAgents(agentsList);

      // Auto-select agent if there's only one and no agent is selected yet
      if (agentsList.length === 1 && !data.agent_id) {
        onChange({ agent_id: agentsList[0].id });
      }
    } catch {
      setAgentsLoadFailed(true);
    } finally {
      setLoadingAgents(false);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!data.name?.trim()) newErrors.name = t('simpleWizard.errors.nameRequired');
    if (!data.agent_id) newErrors.agent = t('simpleWizard.errors.agentRequired');

    if (template.type === 'POSTGRESQL') {
      if (!data.host) newErrors.host = t('simpleWizard.errors.hostRequired');
      if (!data.port) newErrors.port = t('simpleWizard.errors.portRequired');
      if (!data.username) newErrors.username = t('simpleWizard.errors.usernameRequired');
      if (!data.password) newErrors.password = t('simpleWizard.errors.passwordRequired');
      if (!data.database && !testResult?.databases?.length) {
        newErrors.database = t('simpleWizard.errors.databaseRequired');
      }
    } else {
      if (!data.path) newErrors.path = t('simpleWizard.errors.pathRequired');
    }

    setErrors(newErrors);
    const firstInvalidField = Object.keys(newErrors)[0];
    const fieldIds: Record<string, string> = {
      name: 'quick-backup-name',
      agent: 'quick-backup-agent-select',
      host: 'quick-backup-host',
      port: 'quick-backup-port',
      username: 'quick-backup-username',
      password: 'quick-backup-password',
      database: 'quick-backup-database',
      path: 'quick-backup-path',
    };
    if (firstInvalidField && fieldIds[firstInvalidField]) {
      requestAnimationFrame(() => document.getElementById(fieldIds[firstInvalidField])?.focus());
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  const testConnection = async () => {
    const newErrors: Record<string, string> = {};
    if (!data.host) newErrors.host = t('simpleWizard.errors.hostRequired');
    if (!data.port) newErrors.port = t('simpleWizard.errors.portRequired');
    if (!data.username) newErrors.username = t('simpleWizard.errors.usernameRequired');
    if (!data.password) newErrors.password = t('simpleWizard.errors.passwordRequired');
    if (!data.agent_id) newErrors.agent = t('simpleWizard.errors.agentRequired');

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/backups/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          host: data.host,
          port: data.port,
          username: data.username,
          password: data.password,
          agent_id: data.agent_id,
          ssl_mode: 'prefer',
        }),
      });

      const result = await response.json();
      setTestResult(result);

      if (result.success && result.databases) {
        const defaultDb = result.databases.find(
          (db: string) => !['postgres', 'template0', 'template1'].includes(db)
        );
        onChange({ database: defaultDb || '' });
      }
    } catch (_error) {
      setTestResult({
        success: false,
        error: t('simpleWizard.errors.networkError'),
      });
    } finally {
      setTesting(false);
    }
  };

  const selectedAgent = agents.find((a) => a.id === data.agent_id);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
          {t('simpleWizard.details.title')}
        </h3>
        <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
          {template.type === 'POSTGRESQL'
            ? t('simpleWizard.details.descriptionDb')
            : t('simpleWizard.details.descriptionFiles')}
        </p>
      </div>

      <div className="space-y-4">
        <Input
          id="quick-backup-name"
          label={t('simpleWizard.details.nameLabel')}
          placeholder={t('simpleWizard.details.namePlaceholder')}
          value={data.name ?? ''}
          onChange={(e) => {
            onChange({ name: e.target.value });
            setErrors({ ...errors, name: undefined });
          }}
          error={errors.name}
        />

        <div className="relative">
          <label
            id="quick-backup-agent-label"
            className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
          >
            {t('simpleWizard.details.agentLabel')}
          </label>
          <button
            id="quick-backup-agent-select"
            type="button"
            aria-haspopup="menu"
            aria-expanded={showAgentDropdown}
            aria-controls="quick-backup-agent-menu"
            aria-labelledby="quick-backup-agent-label"
            aria-describedby={errors.agent ? 'quick-backup-agent-error' : undefined}
            aria-invalid={errors.agent ? true : undefined}
            onClick={() => {
              const nextOpen = !showAgentDropdown;
              setShowAgentDropdown(nextOpen);
              if (nextOpen) {
                requestAnimationFrame(() =>
                  document
                    .querySelector<HTMLButtonElement>('#quick-backup-agent-menu button')
                    ?.focus()
                );
              }
            }}
            onKeyDown={(event) => {
              if (!showAgentDropdown && ['ArrowDown', 'Enter', ' '].includes(event.key)) {
                event.preventDefault();
                setShowAgentDropdown(true);
                requestAnimationFrame(() =>
                  document
                    .querySelector<HTMLButtonElement>('#quick-backup-agent-menu button')
                    ?.focus()
                );
              }
            }}
            disabled={loadingAgents}
            className={`w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border rounded-xl text-left text-sm font-medium transition-all flex items-center justify-between ${
              errors.agent
                ? 'border-state-danger-light dark:border-state-danger-dark ring-state-danger-light/20 dark:ring-state-danger-dark/20'
                : 'border-surface-border-light dark:border-surface-border-dark'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Server size={16} className="text-text-subtle-light dark:text-text-subtle-dark" />
              <span
                className={`min-w-0 truncate ${
                  selectedAgent
                    ? 'text-slate-900 dark:text-slate-100'
                    : 'text-text-subtle-light dark:text-text-subtle-dark'
                }`}
              >
                {selectedAgent?.name || t('simpleWizard.details.selectAgent')}
              </span>
              {selectedAgent && (
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    selectedAgent.status?.toUpperCase() === 'ONLINE'
                      ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark'
                      : 'bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark'
                  }`}
                >
                  {t(
                    selectedAgent.status?.toUpperCase() === 'ONLINE'
                      ? 'simpleWizard.details.agentOnline'
                      : 'simpleWizard.details.agentOffline'
                  )}
                </span>
              )}
            </div>
            <ChevronDown size={16} className="text-text-subtle-light dark:text-text-subtle-dark" />
          </button>
          {agentsLoadFailed ? (
            <div
              role="alert"
              className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-state-danger-light/30 dark:border-state-danger-dark/30 bg-state-danger-bg-light dark:bg-state-danger-bg-dark px-3 py-2"
            >
              <span className="text-sm text-state-danger-light dark:text-state-danger-dark">
                {t('simpleWizard.errors.agentsLoadFailed')}
              </span>
              <button
                type="button"
                onClick={() => void fetchAgents()}
                className="min-h-11 px-3 text-sm font-semibold text-brand-700 dark:text-brand-300 underline underline-offset-2"
              >
                {t('simpleWizard.buttons.retry')}
              </button>
            </div>
          ) : loadingAgents ? (
            <p
              role="status"
              className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark"
            >
              {t('simpleWizard.details.loadingAgents')}
            </p>
          ) : null}
          {errors.agent && (
            <p
              id="quick-backup-agent-error"
              className="mt-1.5 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1"
            >
              {errors.agent}
            </p>
          )}

          {showAgentDropdown && (
            <div
              id="quick-backup-agent-menu"
              role="menu"
              aria-labelledby="quick-backup-agent-label"
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setShowAgentDropdown(false);
                  document
                    .querySelector<HTMLButtonElement>('[aria-controls="quick-backup-agent-menu"]')
                    ?.focus();
                }
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  const items = Array.from(
                    document.querySelectorAll<HTMLButtonElement>(
                      '#quick-backup-agent-menu [role="menuitemradio"]'
                    )
                  );
                  const current = items.indexOf(document.activeElement as HTMLButtonElement);
                  const direction = event.key === 'ArrowDown' ? 1 : -1;
                  items[(current + direction + items.length) % items.length]?.focus();
                }
              }}
              className="absolute z-10 w-full mt-1 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl shadow-lg max-h-48 overflow-y-auto"
            >
              {agents.length === 0 ? (
                <p className="px-4 py-3 text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('simpleWizard.details.noAgentsAvailable')}
                </p>
              ) : (
                agents.map((agent) => (
                  <button
                    key={agent.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={agent.id === data.agent_id}
                    onClick={() => {
                      onChange({ agent_id: agent.id });
                      setErrors({ ...errors, agent: undefined });
                      setShowAgentDropdown(false);
                    }}
                    className="w-full min-h-11 px-4 py-3 text-left hover:bg-surface-2-light dark:hover:bg-surface-2-dark flex items-center justify-between gap-3"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Server
                        size={16}
                        className="shrink-0 text-text-subtle-light dark:text-text-subtle-dark"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                          {agent.name}
                        </p>
                        {agent.hostname && (
                          <p className="truncate text-xs text-text-muted-light dark:text-text-muted-dark">
                            {agent.hostname}
                          </p>
                        )}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${
                        agent.status?.toUpperCase() === 'ONLINE'
                          ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark'
                          : 'bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark'
                      }`}
                    >
                      {t(
                        agent.status?.toUpperCase() === 'ONLINE'
                          ? 'simpleWizard.details.agentOnline'
                          : 'simpleWizard.details.agentOffline'
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {template.type === 'POSTGRESQL' ? (
          <div className="space-y-4 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                id="quick-backup-host"
                label={t('simpleWizard.details.hostLabel')}
                placeholder="localhost"
                value={data.host ?? ''}
                onChange={(e) => {
                  onChange({ host: e.target.value });
                  setErrors({ ...errors, host: undefined });
                  setTestResult(null);
                }}
                error={errors.host}
              />
              <Input
                id="quick-backup-port"
                label={t('simpleWizard.details.portLabel')}
                type="number"
                placeholder="5432"
                value={data.port?.toString() ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange({ port: val ? parseInt(val) : undefined });
                  setErrors({ ...errors, port: undefined });
                  setTestResult(null);
                }}
                error={errors.port}
              />
            </div>

            <Input
              id="quick-backup-username"
              label={t('simpleWizard.details.usernameLabel')}
              placeholder="postgres"
              value={data.username ?? ''}
              onChange={(e) => {
                onChange({ username: e.target.value });
                setErrors({ ...errors, username: undefined });
                setTestResult(null);
              }}
              error={errors.username}
            />

            <div className="relative">
              <Input
                id="quick-backup-password"
                label={t('simpleWizard.details.passwordLabel')}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={data.password ?? ''}
                onChange={(e) => {
                  onChange({ password: e.target.value });
                  setErrors({ ...errors, password: undefined });
                  setTestResult(null);
                }}
                error={errors.password}
                className="pr-14"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={
                  showPassword
                    ? t('simpleWizard.details.hidePassword')
                    : t('simpleWizard.details.showPassword')
                }
                title={
                  showPassword
                    ? t('simpleWizard.details.hidePassword')
                    : t('simpleWizard.details.showPassword')
                }
                className="absolute right-1 top-[29px] h-11 min-w-11 rounded-lg text-xs font-semibold text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300"
              >
                {showPassword
                  ? t('simpleWizard.details.hidePassword')
                  : t('simpleWizard.details.showPassword')}
              </button>
            </div>

            <button
              type="button"
              onClick={testConnection}
              disabled={testing}
              className="w-full py-2.5 px-4 bg-surface-2-light dark:bg-surface-2-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark text-slate-700 dark:text-slate-300 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {testing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>{t('simpleWizard.details.testing')}</span>
                </>
              ) : (
                <>
                  <Database size={18} />
                  <span>{t('simpleWizard.details.testConnection')}</span>
                </>
              )}
            </button>

            {testResult && (
              <div
                className={`p-4 rounded-xl border ${
                  testResult.success
                    ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20'
                    : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <Check className="text-state-ok-light dark:text-state-ok-dark" size={18} />
                  ) : (
                    <AlertCircle
                      className="text-state-danger-light dark:text-state-danger-dark"
                      size={18}
                    />
                  )}
                  <span
                    className={`font-semibold ${
                      testResult.success
                        ? 'text-state-ok-light dark:text-state-ok-dark'
                        : 'text-state-danger-light dark:text-state-danger-dark'
                    }`}
                  >
                    {testResult.success
                      ? t('simpleWizard.details.connectionSuccess')
                      : t('simpleWizard.details.connectionFailed')}
                  </span>
                </div>

                {testResult.success ? (
                  <div className="text-sm text-slate-600 dark:text-text-subtle-dark space-y-1">
                    <p>{testResult.server_version}</p>
                  </div>
                ) : (
                  <p className="text-sm text-state-danger-light dark:text-state-danger-dark">
                    {testResult.error}
                  </p>
                )}
              </div>
            )}

            {testResult?.success && testResult.databases && (
              <div className="space-y-2">
                <label
                  htmlFor="quick-backup-database"
                  className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark ml-1"
                >
                  {t('simpleWizard.details.databaseLabel')}
                </label>
                <select
                  id="quick-backup-database"
                  value={data.database ?? ''}
                  onChange={(e) => {
                    onChange({ database: e.target.value });
                    setErrors({ ...errors, database: undefined });
                  }}
                  className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand-500 outline-none"
                >
                  <option value="">{t('simpleWizard.details.selectDatabase')}</option>
                  {testResult.databases.map((db) => (
                    <option key={db} value={db}>
                      {db}
                      {['postgres', 'template0', 'template1'].includes(db) ? ' (system)' : ''}
                    </option>
                  ))}
                </select>
                {errors.database && (
                  <p className="text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
                    {errors.database}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
            <div className="w-full">
              <div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <Input
                      id="quick-backup-path"
                      label={t('simpleWizard.details.pathLabel')}
                      placeholder={t('simpleWizard.details.pathPlaceholder')}
                      value={data.path ?? ''}
                      onChange={(e) => {
                        onChange({ path: e.target.value });
                        setErrors({ ...errors, path: undefined });
                      }}
                      error={errors.path}
                    />
                  </div>
                  <div className="flex items-end pb-0.5 sm:pt-6">
                    <button
                      type="button"
                      onClick={() => setIsFolderBrowserOpen(true)}
                      disabled={!data.agent_id}
                      className="min-h-11 w-full sm:w-auto justify-center px-3 sm:px-4 bg-surface-2-light dark:bg-surface-2-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 dark:text-slate-300 rounded-xl font-semibold text-sm transition-colors flex items-center space-x-2 border border-surface-border-light dark:border-surface-border-dark"
                      title={
                        !data.agent_id
                          ? t('simpleWizard.details.selectAgentFirst')
                          : t('simpleWizard.details.browseTooltip')
                      }
                    >
                      <FolderOpen size={18} />
                      <span>{t('simpleWizard.details.browseButton')}</span>
                    </button>
                  </div>
                </div>
                {!data.agent_id && (
                  <p className="mt-1.5 text-xs text-text-muted-light dark:text-text-muted-dark">
                    {t('simpleWizard.details.selectAgentFirst')}
                  </p>
                )}
              </div>
            </div>

            <div className="p-4 border border-surface-border-light dark:border-surface-border-dark rounded-xl">
              <label htmlFor="quick-backup-vss" className="flex items-start cursor-pointer">
                <input
                  id="quick-backup-vss"
                  type="checkbox"
                  checked={data.useVSS || false}
                  onChange={(e) => onChange({ useVSS: e.target.checked })}
                  className="mt-0.5 mr-3"
                />
                <div>
                  <p className="font-medium text-slate-900 dark:text-white text-sm">
                    {t('simpleWizard.details.vssTitle')}
                  </p>
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                    {t('simpleWizard.details.vssDescription')}
                  </p>
                </div>
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
        <button
          type="button"
          onClick={onBack}
          className="min-h-11 px-6 py-2.5 bg-surface-2-light dark:bg-surface-2-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark text-slate-700 dark:text-slate-300 font-bold rounded-xl transition-colors"
        >
          {t('simpleWizard.buttons.back')}
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isCreating}
          className="min-h-11 px-6 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:bg-surface-border-light dark:disabled:bg-surface-border-dark disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors flex items-center space-x-2"
        >
          {isCreating ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>{t('simpleWizard.buttons.creating')}</span>
            </>
          ) : (
            <span>{t('simpleWizard.buttons.next')}</span>
          )}
        </button>
      </div>

      {/* Folder Browser Modal */}
      <FolderBrowserModal
        isOpen={isFolderBrowserOpen}
        onClose={() => setIsFolderBrowserOpen(false)}
        agentId={data.agent_id || ''}
        selectedPaths={data.path ? [data.path] : []}
        onSelectPaths={(newPaths) => {
          if (newPaths.length > 0) {
            onChange({ path: newPaths[0] });
            setErrors({ ...errors, path: undefined });
          }
          setIsFolderBrowserOpen(false);
        }}
        allowMultiple={false}
      />
    </div>
  );
}
